import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { z } from "zod"

const baseParams = z.object({ userId: z.number() })

const paramsSchemas = {
  Chat_UserJoinedGroup: baseParams,
  Chat_UserLeftGroup: baseParams,
  Chat_UserAddedToGroup: baseParams,
  Chat_UserRemovedFromGroup: baseParams,
  Chat_GroupNameChanged: baseParams.extend({
    oldName: z.string(),
    newName: z.string(),
  }),
  Chat_GroupDescriptionChanged: baseParams,
} as const

const payloadSchema = z.discriminatedUnion("key", [
  z.object({
    key: z.literal("Chat_UserJoinedGroup"),
    params: paramsSchemas.Chat_UserJoinedGroup,
  }),
  z.object({
    key: z.literal("Chat_UserLeftGroup"),
    params: paramsSchemas.Chat_UserLeftGroup,
  }),
  z.object({
    key: z.literal("Chat_UserAddedToGroup"),
    params: paramsSchemas.Chat_UserAddedToGroup,
  }),
  z.object({
    key: z.literal("Chat_UserRemovedFromGroup"),
    params: paramsSchemas.Chat_UserRemovedFromGroup,
  }),
  z.object({
    key: z.literal("Chat_GroupNameChanged"),
    params: paramsSchemas.Chat_GroupNameChanged,
  }),
  z.object({
    key: z.literal("Chat_GroupDescriptionChanged"),
    params: paramsSchemas.Chat_GroupDescriptionChanged,
  }),
])

type ChatSystemMessagePayload = z.infer<typeof payloadSchema>

function parseSystemPayload(raw: string): ChatSystemMessagePayload | null {
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return null
  }
  const result = payloadSchema.safeParse(json)
  return result.success ? result.data : null
}

interface SystemMessageProps {
  content: string
}

export function SystemMessage({ content }: SystemMessageProps) {
  const { t } = useTranslation("app_server")

  const parsed = useMemo(() => parseSystemPayload(content), [content])

  const userId = parsed?.params.userId
  const { data: user } = useBatchedSimpleUser(userId)

  const text = useMemo(() => {
    if (!parsed) return content

    const userName = user?.userName ?? t("Unknown")

    switch (parsed.key) {
      case "Chat_UserJoinedGroup":
        return t("Chat_UserJoinedGroup", { userName })
      case "Chat_UserLeftGroup":
        return t("Chat_UserLeftGroup", { userName })
      case "Chat_UserAddedToGroup":
        return t("Chat_UserAddedToGroup", { userName })
      case "Chat_UserRemovedFromGroup":
        return t("Chat_UserRemovedFromGroup", { userName })
      case "Chat_GroupNameChanged":
        return t("Chat_GroupNameChanged", {
          userName,
          oldName: parsed.params.oldName,
          newName: parsed.params.newName,
        })
      case "Chat_GroupDescriptionChanged":
        return t("Chat_GroupDescriptionChanged", { userName })
      default: {
        const _exhaustive: never = parsed
        return content
      }
    }
  }, [parsed, user, t, content])

  return <span>{text}</span>
}
